import React, { useEffect, useContext, useMemo, useReducer, useRef, useState } from 'react'
import * as R from 'ramda'
import {
  Accordion,
  Checkbox as SemanticUiCheckbox,
  Dropdown,
  FormField,
  Form,
  FormGroup,
  Message,
} from 'semantic-ui-react'
import { useParams } from 'react-router-dom'
import { useDispatch, useSelector } from 'react-redux'

import { getSurveyFromId as fetchSurveyToRedux } from 'actions'
import useAxios, { mutate } from 'hooks/useAxios'
import SurveyEmailSettingsContainer from 'components/SurveyEmailSettings/container'
import { draftEditorToHTMLCode, convertHTMLToDraftEditor } from 'components/RichEditor/utils'
import LanguageSelector from 'components/LanguageSelector'
import { languageOption } from 'organisms/FeedbackFilterGroup/constants'
import { Loader } from 'shared/components/Loader'
import { surveySettingContext } from 'pages/SurveySettings/context'
import { toast } from 'libs/toast'
import constants from 'config/constants'

import { getSurveyFromId, getSurveyTokenFromId } from 'reducers'
import SendTestEmailSurveyModal from 'components/SendTestEmailSurveyModal'
import { TooltipLabel } from 'components/TooltipLabel'
import useSaveUserActivityLog, { ACTION_NAME } from 'hooks/useSaveUserActivityLog'

const EMAIL_TYPES = ['initial_email', 'follow_up_email_1', 'follow_up_email_2', 'follow_up_email_3']
const PER_LANGUAGE_FIELDS = ['sender_name', 'subject', 'intro', 'outro']
const DEFAULT_LANGUAGE_CODE = 'en'

const LANGUAGE_NAME_BY_CODE = Object.fromEntries(
  Object.entries(languageOption).map(([name, code]) => [code, name])
)

function resolveLanguageName(code) {
  return LANGUAGE_NAME_BY_CODE[code] || code
}

function deriveServerData(translations) {
  if (!translations || translations.length === 0) {
    return null
  }
  const defaultEntry = translations.find((entry) => entry.is_default) || translations[0]
  return EMAIL_TYPES.reduce((templatesByType, emailType) => {
    if (defaultEntry[emailType]) {
      templatesByType[emailType] = defaultEntry[emailType]
    }
    return templatesByType
  }, {})
}

function buildTemplateForLanguage(serverData, allTranslations, language, defaultLanguageCode) {
  if (!serverData) {
    return {}
  }
  const isDefault = language === defaultLanguageCode
  const languageEntry = (allTranslations || []).find((entry) => entry.value === language) || {}
  const templatesByType = {}
  EMAIL_TYPES.forEach((emailType) => {
    const defaultTemplate = serverData[emailType]
    if (!defaultTemplate) {
      return
    }
    const translation = languageEntry[emailType] || {}
    templatesByType[emailType] = { ...defaultTemplate }
    PER_LANGUAGE_FIELDS.forEach((field) => {
      if (translation[field] != null) {
        templatesByType[emailType][field] = translation[field]
      } else if (isDefault) {
        templatesByType[emailType][field] = defaultTemplate[field]
      } else {
        templatesByType[emailType][field] = ''
      }
    })
  })
  return templatesByType
}

function hasMissingRequiredFields(fields) {
  if (!fields) {
    return false
  }
  const senderName = (fields.sender_name || '').trim()
  const subject = (fields.subject || '').trim()
  const [emailPrefix] = (fields.sender_email || '').split('@')
  const senderEmailPrefix = (emailPrefix || '').trim()
  return !senderName || !subject || !senderEmailPrefix
}

function hasMissingPerLanguageRequiredFields(fields) {
  if (!fields) {
    return false
  }
  const senderName = (fields.sender_name || '').trim()
  const subject = (fields.subject || '').trim()
  return !senderName || !subject
}

function normalizeRichEditorHtml(html) {
  if (!html) {
    return ''
  }
  try {
    return draftEditorToHTMLCode(convertHTMLToDraftEditor(html))
  } catch {
    return html
  }
}

function extractSavedFields(template) {
  const fieldsByType = {}
  EMAIL_TYPES.forEach((emailType) => {
    const fields = template?.[emailType]
    if (fields) {
      fieldsByType[emailType] = {
        email_template: fields.email_template,
        sender_email: fields.sender_email,
        sender_name: fields.sender_name,
        subject: fields.subject,
        intro: normalizeRichEditorHtml(fields.intro),
        outro: normalizeRichEditorHtml(fields.outro),
      }
    }
  })
  return fieldsByType
}

export default function EmailTemplateSettings({ surveyType }) {
  const { surveyId } = useParams()
  const survey = useSurvey()
  const surveyToken = useSurveyToken()
  const dispatch = useDispatch()
  const hasSurveyLoaded = !!survey?.survey_languages

  useEffect(() => {
    if (surveyId && !hasSurveyLoaded) {
      dispatch(fetchSurveyToRedux(surveyId))
    }
  }, [surveyId, hasSurveyLoaded, dispatch])

  const { data: translations } = useAxios({
    url: `survey/survey/${surveyToken}/email-content-translations/`,
    skip: !surveyToken,
  })

  const { data: multilingualPlanFeature, loading: isMultilingualPlanLoading } = useAxios({
    url: 'billing/plans/feature-toggle/',
    method: 'post',
    data: { feature: 'multilingual_survey' },
  })

  if (!hasSurveyLoaded || !translations || isMultilingualPlanLoading) {
    return <Loader withContainer />
  }

  const translationDefault = translations.find((entry) => entry.is_default)
  const initialLanguage = translationDefault?.value || DEFAULT_LANGUAGE_CODE
  const isMultilingualPlanEnabled = R.path(['enabled'], multilingualPlanFeature)

  return (
    <EmailTemplateSettingsBody
      surveyType={surveyType}
      initialLanguage={initialLanguage}
      translations={translations}
      isMultilingualPlanEnabled={isMultilingualPlanEnabled}
    />
  )
}

function EmailTemplateSettingsBody({
  surveyType,
  initialLanguage,
  translations,
  isMultilingualPlanEnabled,
}) {
  const [template, setTemplate] = useSetState({})
  const [serverData, setServerDataState] = useState({})
  const [sendTestModalOpen, setSendTestModalOpen] = useState(false)
  const [templateSelectedToSendTest, setTemplateSelectedToSendTest] = useState(null)
  const [receiverEmails, setReceiverEmails] = useState('')
  const [inputEmailError, setInputEmailError] = useState(false)
  const [inputMaxEmailError, setInputMaxEmailError] = useState(false)
  const [followUpOption, setFollowUpOption] = useState('')
  const [savedTemplate, setSavedTemplate] = useState(null)
  const [savedFollowUpOption, setSavedFollowUpOption] = useState(null)

  const surveyToken = useSurveyToken()
  const isMultilingualActive = isMultilingualPlanEnabled
  const survey = useSurvey()
  const [selectedLanguage, setSelectedLanguage] = useState(initialLanguage)
  const shouldRequestLanguage =
    isMultilingualActive && !!survey?.is_multilingual_survey_enabled && selectedLanguage
  const emailTemplatesUrl = shouldRequestLanguage
    ? `survey/survey/${surveyToken}/email-templates/?language=${selectedLanguage}`
    : `survey/survey/${surveyToken}/email-templates/`
  const getTemplates = useAxios({
    url: emailTemplatesUrl,
    skip: surveyToken === undefined,
  })
  const previousDefaultLanguageRef = useRef(undefined)
  const getEmailDomain = useAxios({
    url: `sendgrid/whitelabel/domains?limit=500`,
  })

  const { data: emailSurveySetting } = useAxios({
    url: `survey/survey/${surveyToken}/email-survey-setting/`,
    skip: !surveyToken,
  })

  const { fetch: saveEmailSurveySetting } = useAxios({
    method: 'patch',
    url: `survey/survey/${surveyToken}/email-survey-setting/`,
    skip: true,
  })
  const { fetch: sendTestEmailSurvey, loading: sendTestEmailSurveyLoading } = useAxios({
    method: 'post',
    url: `survey/survey/${surveyToken}/send-test-email/`,
    skip: true,
  })
  const { fetch: updateTemplateSetting } = useAxios({
    url: `survey/survey/${surveyToken}/email-content-settings/`,
    method: 'put',
    skip: true,
  })
  const { data: userInfo } = useAxios({
    url: 'account/users/me/v2/',
  })

  const [followUpEmailEnableList, setFollowUpEmailEnable] = useFollowUpEmailEnableListState(
    R.prop('follow_up_enable', emailSurveySetting),
    saveEmailSurveySetting
  )

  const saveUserActivityLog = useSaveUserActivityLog()
  const { surveyId } = useParams()

  const DEFAULT_ACTIVE_INDEX = 0
  const [activeIndex, setActiveIndex] = useState(DEFAULT_ACTIVE_INDEX)
  function handleTitleClick(_, { index: titleIndex }) {
    setActiveIndex((activeIndex) => {
      if (titleIndex === activeIndex) {
        const COLLAPSE_ALL_ACCORDION_INDEX = -1
        return COLLAPSE_ALL_ACCORDION_INDEX
      }

      return titleIndex
    })
  }

  const availableLanguages = useMemo(
    () =>
      (survey?.survey_languages || []).map((entry) => ({
        value: entry.language,
        name: resolveLanguageName(entry.language),
        isDefault: !!entry.is_default,
        isActive: !!entry.is_active,
      })),
    [survey]
  )
  const isLanguageSelectorVisible =
    isMultilingualActive &&
    !!survey?.is_multilingual_survey_enabled &&
    availableLanguages.length > 0

  const defaultSurveyLanguage =
    availableLanguages.find((entry) => entry.isDefault) || availableLanguages[0]
  const defaultLanguageCode = defaultSurveyLanguage?.value || DEFAULT_LANGUAGE_CODE
  const selectedLanguageEntry = availableLanguages.find((entry) => entry.value === selectedLanguage)
  const isDefaultLanguage =
    availableLanguages.length === 0 || selectedLanguage === defaultLanguageCode
  const isSelectedLanguageActive = !!selectedLanguageEntry?.isActive
  const anyRequiredFieldMissing = EMAIL_TYPES.some((emailType) =>
    hasMissingRequiredFields(template[emailType])
  )
  const isSaveDisabled = isDefaultLanguage && anyRequiredFieldMissing
  const calloutKindByType = useMemo(() => {
    if (isDefaultLanguage) {
      return {}
    }
    return EMAIL_TYPES.reduce((acc, emailType) => {
      const isEmpty = hasMissingPerLanguageRequiredFields(template[emailType])
      if (!isSelectedLanguageActive) {
        acc[emailType] = isEmpty ? 'inactive-empty' : 'inactive-only'
      } else {
        acc[emailType] = isEmpty ? 'fill-in-only' : null
      }
      return acc
    }, {})
  }, [isDefaultLanguage, isSelectedLanguageActive, template])

  useEffect(() => {
    if (!translations || translations.length === 0) {
      return
    }
    const newServerData = deriveServerData(translations)
    setServerDataState(newServerData)
    const newTemplate = isLanguageSelectorVisible
      ? buildTemplateForLanguage(newServerData, translations, selectedLanguage, defaultLanguageCode)
      : newServerData
    setTemplate(newTemplate)
    setSavedTemplate(extractSavedFields(newTemplate))
  }, [translations, isLanguageSelectorVisible, setTemplate, selectedLanguage, defaultLanguageCode])

  useEffect(() => {
    if (userInfo) {
      setReceiverEmails(userInfo.email)
    }
  }, [userInfo, setReceiverEmails])

  useEffect(() => {
    if (emailSurveySetting && !followUpOption) {
      setFollowUpOption(emailSurveySetting.follow_up_option)
      setSavedFollowUpOption(emailSurveySetting.follow_up_option)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [emailSurveySetting])

  const isDirty = useMemo(() => {
    if (savedTemplate === null) {
      return false
    }
    if (savedFollowUpOption !== null && followUpOption !== savedFollowUpOption) {
      return true
    }
    return !R.equals(extractSavedFields(template), savedTemplate)
  }, [template, followUpOption, savedTemplate, savedFollowUpOption])

  useEffect(() => {
    if (availableLanguages.length === 0) {
      return
    }
    const previousDefault = previousDefaultLanguageRef.current
    if (defaultLanguageCode === previousDefault) {
      return
    }
    if (previousDefault === undefined || selectedLanguage === previousDefault) {
      setSelectedLanguage(defaultLanguageCode)
    }
    previousDefaultLanguageRef.current = defaultLanguageCode
  }, [defaultLanguageCode, availableLanguages, selectedLanguage])

  useEffect(() => {
    if (availableLanguages.length === 0) {
      return
    }
    const availableCodes = availableLanguages.map((entry) => entry.value)
    if (!availableCodes.includes(selectedLanguage)) {
      const defaultEntry = availableLanguages.find((entry) => entry.isDefault)
      setSelectedLanguage(defaultEntry ? defaultEntry.value : availableCodes[0])
    }
  }, [availableLanguages, selectedLanguage])

  if (!getTemplates.data || !getEmailDomain.data || !emailSurveySetting) {
    return <Loader withContainer />
  }

  if (!template) {
    return <p>Oops! Something went wrong. Please contact Simplesat support for help.</p>
  }

  const domains = getEmailDomain.data
  const templates = getTemplates.data

  function _buildPutBody() {
    const shouldSaveAsDefaultEmailTemplateData =
      !isMultilingualPlanEnabled || !isMultilingualActive || !survey?.is_multilingual_survey_enabled
    const data = {
      follow_up_option: followUpOption,
      translation: shouldSaveAsDefaultEmailTemplateData ? null : selectedLanguage,
    }

    EMAIL_TYPES.forEach((emailType) => {
      const current = template[emailType]
      if (!current) {
        return
      }
      data[emailType] = {
        email_template: current.email_template,
        sender_email: current.sender_email,
        sender_name: current.sender_name,
        subject: current.subject,
        intro: current.intro,
        outro: current.outro,
      }
    })
    return data
  }

  function _refreshAfterSave() {
    mutate(`survey/survey/${surveyToken}/email-content-translations/`)
  }

  function onSubmit() {
    const data = _buildPutBody()
    return updateTemplateSetting({ data })
      .then(() => {
        _refreshAfterSave()
        setSavedTemplate(extractSavedFields(template))
        setSavedFollowUpOption(followUpOption)
        toast.success('Your settings were successfully updated.')
        saveUserActivityLog({
          action: ACTION_NAME.edit_survey_publish_email_page,
          target: `sur_${surveyId}`,
          requestBody: data,
          statusCode: 200,
        })
      })
      .catch((err) => {
        toast.error('Something went wrong, please try again.')
        saveUserActivityLog({
          action: ACTION_NAME.edit_survey_publish_email_page,
          target: `sur_${surveyId}`,
          requestBody: data,
          statusCode: err.status,
        })
        throw err
      })
  }

  function handleLanguageChange(nextLanguage) {
    if (nextLanguage === selectedLanguage) {
      return
    }
    if (isDirty) {
      const confirmed = window.confirm(
        'Are you sure you want to leave? Changes you made may not be saved.'
      )
      if (!confirmed) {
        return
      }
    }
    setSelectedLanguage(nextLanguage)
    if (isMultilingualActive) {
      const newTemplate = buildTemplateForLanguage(
        serverData,
        translations,
        nextLanguage,
        defaultLanguageCode
      )
      setTemplate(newTemplate)
      setSavedTemplate(extractSavedFields(newTemplate))
    }
  }

  function handleSendTestEmailSurvey() {
    setInputEmailError(false)
    setInputMaxEmailError(false)
    const receivers = receiverEmails.replace(/\s/g, '').split(',')
    if (receivers.length > 10) {
      return setInputMaxEmailError(true)
    }
    const isEmailsValid = receivers.every((email) => constants.EMAIL_REGEX.test(email))

    if (isEmailsValid) {
      const templateData = template[templateSelectedToSendTest] || {}
      const baseDefault = serverData[templateSelectedToSendTest] || {}
      const isNonDefaultLanguage = selectedLanguage !== defaultLanguageCode
      const trimmedSenderName = (templateData.sender_name || '').trim()
      const trimmedSubject = (templateData.subject || '').trim()
      const senderName =
        isNonDefaultLanguage && !trimmedSenderName
          ? baseDefault.sender_name
          : templateData.sender_name
      const subject =
        isNonDefaultLanguage && !trimmedSubject ? baseDefault.subject : templateData.subject
      const data = {
        ...templateData,
        sender_name: senderName,
        subject,
        receiver_emails: receivers,
        language: selectedLanguage,
      }
      delete data.html
      sendTestEmailSurvey({ data })
        .then(() => {
          setSendTestModalOpen(false)
          toast.success(`We've sent a test email.`)
        })
        .catch((error) => {
          const isTestEmailLimitReachedViaCloudflare =
            error.message.includes('Network Error') ||
            error.message.includes('net::ERR') ||
            error.response.status == 429

          const isSubscriptionStatusIsCanceled =
            error.response.data.detail == 'Please choose a plan before sending test emails.'

          if (isSubscriptionStatusIsCanceled) {
            toast.error('Please choose a plan before sending test emails.')
          } else if (isTestEmailLimitReachedViaCloudflare) {
            toast.error('Test email limit reached. Please wait 1 hour before trying again.')
          } else {
            toast.error('Something went wrong, please try again.')
          }
        })
      setInputEmailError(false)
    } else {
      setInputEmailError(true)
    }
  }

  function handleSendTestModalOpen(emailType) {
    setSendTestModalOpen(true)
    setTemplateSelectedToSendTest(emailType)
  }

  function _renderCalloutFor(emailType) {
    const calloutKind = calloutKindByType[emailType]
    if (calloutKind === 'fill-in-only') {
      return (
        <WarningCallout show>
          <Message.List>
            <Message.Item>
              To send emails in this language, fill in <b>From</b> and <b>Subject</b>. Otherwise,
              emails send in the default language.
            </Message.Item>
          </Message.List>
        </WarningCallout>
      )
    }
    if (calloutKind === 'inactive-only') {
      return (
        <WarningCallout show>
          <Message.List>
            <Message.Item>
              This language is inactive for live sending. You can still edit and send test emails.
              Activate it in Configure to start sending.
            </Message.Item>
          </Message.List>
        </WarningCallout>
      )
    }
    if (calloutKind === 'inactive-empty') {
      return (
        <WarningCallout show>
          <Message.List>
            <Message.Item>
              To send emails in this language, fill in <b>From</b> and <b>Subject</b>. Otherwise,
              emails send in the default language.
            </Message.Item>
            <Message.Item>
              This language is inactive for live sending. You can still edit and send test emails.
              Activate it in Configure to start sending.
            </Message.Item>
          </Message.List>
        </WarningCallout>
      )
    }
    return null
  }

  function _generateContent(emailType) {
    const isTemplateExist = R.prop(emailType, template)
    if (isTemplateExist) {
      return (
        <SurveyEmailSettingsContainer
          key={`${emailType}:${selectedLanguage}`}
          onChange={(data) => setTemplate({ [emailType]: data })}
          defaultTemplate={template[emailType]}
          templates={templates}
          domains={domains}
          surveyType={surveyType}
          handleSendTestModalOpen={() => handleSendTestModalOpen(emailType)}
          isDefaultLanguage={isDefaultLanguage}
        />
      )
    }
    return (
      <Message negative>
        <strong>Whoops!</strong> Something went wrong. Please contact Simplesat support to help you
        out.
      </Message>
    )
  }

  function _generateTitle(
    emailType,
    text,
    isFollowUpEmailEnabled,
    onIsFollowUpEmailEnabledChanged
  ) {
    if (emailType === 'initial_email') {
      return (
        <TooltipLabel
          label={text}
          descriptionTitle="Initial email"
          descriptionContent={
            <React.Fragment>
              The first message sent in a one-off or ongoing email sequence.
              <br />
              <br />
              These settings give you a chance to configure sender details, subject, content, and
              the design template.
            </React.Fragment>
          }
          learnMoreLink="https://help.simplesat.io/en/articles/2220503"
        />
      )
    }

    const isTemplateExist = R.prop(emailType, template)
    if (isTemplateExist) {
      return (
        <span>
          <label>{text}</label>
          <Checkbox
            defaultChecked={isFollowUpEmailEnabled}
            onChange={onIsFollowUpEmailEnabledChanged}
          />
        </span>
      )
    }

    return (
      <span>
        <span>{text}</span>
        <i className="ml1 far fa-exclamation-triangle text-orange" />
      </span>
    )
  }

  return (
    <React.Fragment>
      <SideEffectHandler
        saveFunction={onSubmit}
        isSaveDisabled={isSaveDisabled}
        isDirty={isDirty}
      />
      <SendTestEmailSurveyModal
        modalOpen={sendTestModalOpen}
        receiverEmails={receiverEmails}
        setReceiverEmails={setReceiverEmails}
        handleCloseModal={() => setSendTestModalOpen(false)}
        handleSendTestEmailSurvey={handleSendTestEmailSurvey}
        inputEmailError={inputEmailError}
        inputMaxEmailError={inputMaxEmailError}
        isSendTestEmailSurveyButtonLoading={sendTestEmailSurveyLoading}
        showDefaultLanguageWarning={
          templateSelectedToSendTest != null &&
          selectedLanguage !== defaultLanguageCode &&
          hasMissingPerLanguageRequiredFields(template[templateSelectedToSendTest])
        }
      />
      {isLanguageSelectorVisible && (
        <div className="!mb-4 flex items-center justify-between">
          <label className="!font-bold">Email language</label>
          <LanguageSelector
            value={selectedLanguage}
            onChange={handleLanguageChange}
            options={availableLanguages}
          />
        </div>
      )}
      <Accordion fluid styled exclusive={false}>
        <Accordion.Title active={activeIndex === 0} index={0} onClick={handleTitleClick}>
          {_generateTitle('initial_email', 'Initial email')}
        </Accordion.Title>
        <Accordion.Content active={activeIndex === 0}>
          {_renderCalloutFor('initial_email')}
          {_generateContent('initial_email')}
        </Accordion.Content>
      </Accordion>

      <Form>
        <FormGroup className="!flex !flex-col !mt-4">
          <FormField>
            <TooltipLabel
              label={'Follow-up emails'}
              descriptionTitle="Follow-up emails"
              descriptionContent={
                <React.Fragment>
                  Boost response rates by sending gentle reminders to customers who didn't open or
                  respond to your survey.
                  <br />
                  <br />
                  Choose whether to follow up based on unopened emails or unanswered surveys—it's up
                  to how you want to measure engagement.
                </React.Fragment>
              }
              learnMoreLink={
                'https://help.simplesat.io/en/articles/' +
                '2220503-direct-email-surveys#follow-up-reminders'
              }
            />
          </FormField>
          <FormField inline>
            <label className="!font-normal">Send follow-ups to</label>
            <Dropdown
              selection
              value={followUpOption}
              onChange={(_, { value }) => {
                setFollowUpOption(value)
              }}
              options={[
                {
                  key: 'unopened',
                  text: 'Unopened customers',
                  value: 'unopened',
                },
                {
                  key: 'unanswered',
                  text: 'Unanswered customers',
                  value: 'unanswered',
                },
              ]}
            />
          </FormField>
        </FormGroup>
      </Form>

      <Accordion fluid styled exclusive={false}>
        <Accordion.Title active={activeIndex === 1} index={1} onClick={handleTitleClick}>
          {_generateTitle(
            'follow_up_email_1',
            `Send ${
              R.path(['follow_up_day', 0], emailSurveySetting) || 2
            } business days later if ${followUpOption}`,
            followUpEmailEnableList[0],
            (enabled) => {
              const titleIndex = 1
              setActiveIndex(titleIndex)
              setFollowUpEmailEnable(enabled, titleIndex - 1)
            }
          )}
        </Accordion.Title>
        <Accordion.Content active={activeIndex === 1}>
          {_renderCalloutFor('follow_up_email_1')}
          {_generateContent('follow_up_email_1')}
        </Accordion.Content>
        <Accordion.Title active={activeIndex === 2} index={2} onClick={handleTitleClick}>
          {_generateTitle(
            'follow_up_email_2',
            `Send ${
              R.path(['follow_up_day', 1], emailSurveySetting) || 5
            } business days later if ${followUpOption}`,
            followUpEmailEnableList[1],
            (enabled) => {
              const titleIndex = 2
              setActiveIndex(titleIndex)
              setFollowUpEmailEnable(enabled, titleIndex - 1)
            }
          )}
        </Accordion.Title>
        <Accordion.Content active={activeIndex === 2}>
          {_renderCalloutFor('follow_up_email_2')}
          {_generateContent('follow_up_email_2')}
        </Accordion.Content>
        <Accordion.Title active={activeIndex === 3} index={3} onClick={handleTitleClick}>
          {_generateTitle(
            'follow_up_email_3',
            `Send ${
              R.path(['follow_up_day', 2], emailSurveySetting) || 10
            } business days later if ${followUpOption}`,
            followUpEmailEnableList[2],
            (enabled) => {
              const titleIndex = 3
              setActiveIndex(titleIndex)
              setFollowUpEmailEnable(enabled, titleIndex - 1)
            }
          )}
        </Accordion.Title>
        <Accordion.Content active={activeIndex === 3}>
          {_renderCalloutFor('follow_up_email_3')}
          {_generateContent('follow_up_email_3')}
        </Accordion.Content>
      </Accordion>
    </React.Fragment>
  )
}

function SideEffectHandler({ saveFunction, isSaveDisabled, isDirty }) {
  const { setContext } = useContext(surveySettingContext)

  useEffect(() => {
    setContext({
      currentSaveFunction: saveFunction,
      isDisabled: isSaveDisabled,
      isSurveyBuilderDirty: isDirty,
    })
    return () => {
      setContext({ isDisabled: false })
    }
  }, [saveFunction, setContext, isSaveDisabled, isDirty])

  return null
}

function WarningCallout({ show, children }) {
  if (!show) {
    return null
  }
  return (
    <Message warning size="small" className="!mb-3">
      {children}
    </Message>
  )
}

function Checkbox({ defaultChecked, onChange }) {
  const disabled = false

  function handleChange(_, { checked }) {
    onChange(checked)
  }

  function handleCheckboxClick(e) {
    e.stopPropagation()
  }

  return (
    <SemanticUiCheckbox
      toggle
      style={{ float: 'right' }}
      onClick={handleCheckboxClick}
      defaultChecked={defaultChecked}
      onChange={handleChange}
      disabled={disabled}
    />
  )
}

function useSurveyToken() {
  const { surveyId } = useParams()
  return useSelector((state) => getSurveyTokenFromId(state, surveyId))
}

function useSurvey() {
  const { surveyId } = useParams()
  return useSelector((state) => getSurveyFromId(state, surveyId))
}

function useSetState(initialState) {
  const [state, setState] = useReducer(
    (state, newState) => ({ ...state, ...newState }),
    initialState
  )
  return [state, setState]
}

const defaultFollowUpEmailEnableList = [false, false, false]
function useFollowUpEmailEnableListState(
  value = defaultFollowUpEmailEnableList,
  saveEmailSurveySetting
) {
  const [followUpEmailEnableList, setFollowUpEmailEnableList] = useState(value)
  const surveyToken = useSurveyToken()

  const saveUserActivityLog = useSaveUserActivityLog()
  const { surveyId } = useParams()

  useEffect(() => {
    setFollowUpEmailEnableList(value)
  }, [value])

  function setFollowUpEmailEnable(enabled, followUpEmailIndex) {
    setFollowUpEmailEnableList((enableList) => {
      const newEnableList = [...enableList]
      newEnableList[followUpEmailIndex] = enabled

      const data = {
        follow_up_enable: newEnableList,
      }
      saveEmailSurveySetting({
        data,
      })
        .then((response) => {
          mutate(`survey/survey/${surveyToken}/email-survey-setting/`, response.data, false)
          if (enabled) {
            toast.success(`Follow-up email ${followUpEmailIndex + 1} successfully enabled.`)
          } else {
            toast.success(`Follow-up email ${followUpEmailIndex + 1} successfully disabled.`)
          }
          saveUserActivityLog({
            action: ACTION_NAME.toggle_follow_up_email_survey,
            target: `sur_${surveyId}`,
            requestBody: data,
            statusCode: response.status,
          })
        })
        .catch((error) => {
          toast.error('Oops! Something went wrong. Please contact Simplesat support for help.')
          saveUserActivityLog({
            action: ACTION_NAME.toggle_follow_up_email_survey,
            target: `sur_${surveyId}`,
            requestBody: data,
            statusCode: error.response.status,
          })
        })

      return newEnableList
    })
  }

  return [followUpEmailEnableList, setFollowUpEmailEnable]
}



// WEBPACK FOOTER //
// ./node_modules/eslint-loader??ref--0-0!./src/components/EmailTemplateSettings/index.js